import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  RefreshIcon,
  CheckIcon,
  CopyIcon,
  TrashIcon,
  SettingsIcon,
  PhoneIcon,
  StarIcon,
  PlayIcon,
  PauseIcon,
} from '../icons';
import {
  type ConnectedNumber,
  numberLabel,
} from '../../lib/whatsappApi';
import {
  getQualityRatingDisplay,
  formatMessagingLimitTier,
  getNameStatusDisplay,
  formatRelativeTime,
} from '../../lib/connectHelpers';

export interface PhoneNumberTableProps {
  numbers: ConnectedNumber[];
  defaultId: string | null;
  manageId: string | null;
  busyId: string | null;
  onManage: (id: string) => void;
  onRefreshHealth: (id: string) => Promise<void>;
  onMakeDefault: (id: string) => Promise<void>;
  onTogglePause: (id: string, currentlyPaused: boolean) => Promise<void>;
  onRemove: (number: ConnectedNumber) => Promise<void>;
}

export function PhoneNumberTable({
  numbers,
  defaultId,
  manageId,
  busyId,
  onManage,
  onRefreshHealth,
  onMakeDefault,
  onTogglePause,
  onRemove,
}: PhoneNumberTableProps) {
  const { t } = useI18n();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  function copyText(id: string, text: string) {
    if (!text) return;
    void navigator.clipboard?.writeText(text);
    setCopiedId(id);
    window.setTimeout(() => setCopiedId(null), 1500);
  }

  function renderRelative(timestamp: number | null | undefined) {
    const rel = formatRelativeTime(timestamp);
    return t(rel.key, rel.params);
  }

  if (numbers.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-ink-3 dark:text-slate-400">
        {t('connect.table.empty')}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-card border border-line bg-white shadow-card dark:border-slate-800 dark:bg-slate-900">
      {/* Desktop Table View (>= 768px) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-start text-xs">
          <thead className="border-b border-line bg-surface/50 text-ink-3 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
            <tr>
              <th className="px-4 py-3 text-start font-semibold">{t('connect.table.number')}</th>
              <th className="px-4 py-3 text-start font-semibold">{t('connect.table.verifiedName')}</th>
              <th className="px-4 py-3 text-start font-semibold">{t('connect.table.status')}</th>
              <th className="px-4 py-3 text-start font-semibold">{t('connect.table.quality')}</th>
              <th className="px-4 py-3 text-start font-semibold">{t('connect.table.tier')}</th>
              <th className="px-4 py-3 text-start font-semibold">{t('connect.table.nameStatus')}</th>
              <th className="px-4 py-3 text-start font-semibold">{t('connect.table.phoneId')}</th>
              <th className="px-4 py-3 text-start font-semibold">{t('connect.table.lastSync')}</th>
              <th className="px-4 py-3 text-end font-semibold">{t('connect.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line dark:divide-slate-800">
            {numbers.map((n) => {
              const isDefault = n.id === defaultId;
              const isSelected = n.id === manageId;
              const isBusy = n.id === busyId;
              const health = n.health;
              const isPaused = Boolean(n.isPaused);
              const isLive = Boolean(n.isLive && n.hasToken);

              const quality = getQualityRatingDisplay(health?.qualityRating);
              const tier = formatMessagingLimitTier(health?.messagingLimitTier);
              const nameStatus = getNameStatusDisplay(health?.nameStatus);
              const phoneDisplay = health?.displayPhoneNumber || n.verifiedPhoneE164 || numberLabel(n);
              const verifiedName = health?.verifiedName || n.label || '—';

              return (
                <tr
                  key={n.id}
                  className={cn(
                    'transition-colors hover:bg-surface/40 dark:hover:bg-slate-800/30',
                    isSelected && 'bg-brand-50/40 dark:bg-brand-950/20',
                  )}
                >
                  {/* Phone Number */}
                  <td className="px-4 py-3 font-semibold text-ink dark:text-slate-200">
                    <div className="flex items-center gap-2">
                      <span dir="ltr" className="font-mono text-[13px]">{phoneDisplay}</span>
                      {isDefault && (
                        <Badge variant="brand" size="sm" dot>
                          {t('connect.action.default')}
                        </Badge>
                      )}
                    </div>
                  </td>

                  {/* Verified Name */}
                  <td className="px-4 py-3 text-ink-2 dark:text-slate-300">
                    <span className="truncate max-w-[140px] inline-block" title={verifiedName}>
                      {verifiedName}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    {isPaused ? (
                      <Badge variant="warning" size="sm" dot>
                        {t('connect.waba.pausedBadge')}
                      </Badge>
                    ) : isLive ? (
                      <Badge variant="success" size="sm" dot>
                        {t('connect.status.connected')}
                      </Badge>
                    ) : (
                      <Badge variant="danger" size="sm" dot>
                        {t('connect.status.error')}
                      </Badge>
                    )}
                  </td>

                  {/* Quality Rating */}
                  <td className="px-4 py-3">
                    <Badge variant={quality.variant} size="sm" dot={quality.dot}>
                      {t(quality.labelKey)}
                    </Badge>
                  </td>

                  {/* Limit Tier */}
                  <td className="px-4 py-3">
                    <span className="font-medium text-ink-2 dark:text-slate-300">
                      {tier.labelKey ? t(tier.labelKey) : tier.text}
                    </span>
                  </td>

                  {/* Name Status */}
                  <td className="px-4 py-3">
                    <Badge variant={nameStatus.variant} size="sm">
                      {t(nameStatus.labelKey)}
                    </Badge>
                  </td>

                  {/* Phone Number ID */}
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => copyText(n.id, n.phoneNumberId)}
                      className="group inline-flex items-center gap-1 font-mono text-[11px] text-ink-3 hover:text-brand dark:text-slate-400 dark:hover:text-brand-tint"
                      title={n.phoneNumberId}
                    >
                      <span className="truncate max-w-[80px]">...{n.phoneNumberId.slice(-6)}</span>
                      {copiedId === n.id ? (
                        <CheckIcon size={12} className="text-success" />
                      ) : (
                        <CopyIcon size={12} className="opacity-60 group-hover:opacity-100" />
                      )}
                    </button>
                  </td>

                  {/* Last Sync */}
                  <td className="px-4 py-3 text-ink-3 dark:text-slate-400 whitespace-nowrap">
                    {renderRelative(health?.lastSyncAt || n.updatedAt)}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-end">
                    <div className="inline-flex items-center justify-end gap-1">
                      {/* Refresh Health */}
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => void onRefreshHealth(n.id)}
                        className="rounded-button p-1.5 text-ink-3 hover:bg-surface hover:text-ink disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                        title={t('connect.action.refreshHealth')}
                      >
                        <RefreshIcon size={14} className={isBusy ? 'animate-spin' : ''} />
                      </button>

                      {/* Pause / Resume */}
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => void onTogglePause(n.id, isPaused)}
                        className={cn(
                          'rounded-button p-1.5 text-ink-3 hover:bg-surface disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800',
                          isPaused ? 'text-brand hover:text-brand-tint' : 'hover:text-warning'
                        )}
                        title={isPaused ? t('connect.action.resume') : t('connect.action.pause')}
                      >
                        {isPaused ? <PlayIcon size={14} /> : <PauseIcon size={14} />}
                      </button>

                      {/* Make Default */}
                      {!isDefault && (
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => void onMakeDefault(n.id)}
                          className="rounded-button p-1.5 text-ink-3 hover:bg-surface hover:text-brand disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800"
                          title={t('connect.action.makeDefault')}
                        >
                          <StarIcon size={14} />
                        </button>
                      )}

                      {/* Manage */}
                      <button
                        type="button"
                        onClick={() => onManage(n.id)}
                        className={cn(
                          'rounded-button p-1.5 text-ink-3 hover:bg-surface hover:text-ink dark:text-slate-400 dark:hover:bg-slate-800',
                          isSelected && 'bg-brand text-white hover:bg-brand dark:bg-brand dark:text-white'
                        )}
                        title={t('connect.action.manage')}
                      >
                        <SettingsIcon size={14} />
                      </button>

                      {/* Remove */}
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => void onRemove(n)}
                        className="rounded-button p-1.5 text-ink-3 hover:bg-danger-50 hover:text-danger disabled:opacity-40 dark:text-slate-400 dark:hover:bg-rose-950/40 dark:hover:text-rose-400"
                        title={t('connect.action.remove')}
                      >
                        <TrashIcon size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View (< 768px, e.g. 375px) */}
      <div className="divide-y divide-line md:hidden dark:divide-slate-800">
        {numbers.map((n) => {
          const isDefault = n.id === defaultId;
          const isBusy = n.id === busyId;
          const health = n.health;
          const isPaused = Boolean(n.isPaused);
          const isLive = Boolean(n.isLive && n.hasToken);

          const quality = getQualityRatingDisplay(health?.qualityRating);
          const tier = formatMessagingLimitTier(health?.messagingLimitTier);
          const nameStatus = getNameStatusDisplay(health?.nameStatus);
          const phoneDisplay = health?.displayPhoneNumber || n.verifiedPhoneE164 || numberLabel(n);
          const verifiedName = health?.verifiedName || n.label || '—';

          return (
            <div key={n.id} className="p-3.5 space-y-2.5">
              {/* Top row: Phone + Default + Status */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 font-mono text-sm font-bold text-ink dark:text-slate-200">
                  <PhoneIcon size={14} className="text-brand shrink-0" />
                  <span dir="ltr">{phoneDisplay}</span>
                  {isDefault && (
                    <Badge variant="brand" size="sm">
                      {t('connect.action.default')}
                    </Badge>
                  )}
                </div>
                <div>
                  {isPaused ? (
                    <Badge variant="warning" size="sm" dot>
                      {t('connect.waba.pausedBadge')}
                    </Badge>
                  ) : isLive ? (
                    <Badge variant="success" size="sm" dot>
                      {t('connect.status.connected')}
                    </Badge>
                  ) : (
                    <Badge variant="danger" size="sm" dot>
                      {t('connect.status.error')}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="block text-[11px] text-ink-3 dark:text-slate-400">
                    {t('connect.table.verifiedName')}
                  </span>
                  <span className="font-semibold text-ink dark:text-slate-200 truncate block">
                    {verifiedName}
                  </span>
                </div>
                <div>
                  <span className="block text-[11px] text-ink-3 dark:text-slate-400">
                    {t('connect.table.quality')}
                  </span>
                  <Badge variant={quality.variant} size="sm" dot={quality.dot}>
                    {t(quality.labelKey)}
                  </Badge>
                </div>
                <div>
                  <span className="block text-[11px] text-ink-3 dark:text-slate-400">
                    {t('connect.table.tier')}
                  </span>
                  <span className="font-medium text-ink-2 dark:text-slate-300">
                    {tier.labelKey ? t(tier.labelKey) : tier.text}
                  </span>
                </div>
                <div>
                  <span className="block text-[11px] text-ink-3 dark:text-slate-400">
                    {t('connect.table.nameStatus')}
                  </span>
                  <Badge variant={nameStatus.variant} size="sm">
                    {t(nameStatus.labelKey)}
                  </Badge>
                </div>
              </div>

              {/* ID & Last Sync */}
              <div className="flex items-center justify-between text-[11px] text-ink-3 dark:text-slate-400 pt-1 border-t border-line/60 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => copyText(n.id, n.phoneNumberId)}
                  className="inline-flex items-center gap-1 font-mono hover:text-brand"
                >
                  <span>ID: ...{n.phoneNumberId.slice(-6)}</span>
                  {copiedId === n.id ? (
                    <CheckIcon size={12} className="text-success" />
                  ) : (
                    <CopyIcon size={12} />
                  )}
                </button>
                <span>{renderRelative(health?.lastSyncAt || n.updatedAt)}</span>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onManage(n.id)}
                  className="flex-1 text-xs"
                >
                  <SettingsIcon size={13} className="me-1" />
                  {t('connect.action.manage')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => void onRefreshHealth(n.id)}
                  title={t('connect.action.refreshHealth')}
                >
                  <RefreshIcon size={13} className={isBusy ? 'animate-spin' : ''} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => void onTogglePause(n.id, isPaused)}
                  title={isPaused ? t('connect.action.resume') : t('connect.action.pause')}
                >
                  {isPaused ? <PlayIcon size={13} /> : <PauseIcon size={13} />}
                </Button>
                {!isDefault && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={isBusy}
                    onClick={() => void onMakeDefault(n.id)}
                    title={t('connect.action.makeDefault')}
                  >
                    <StarIcon size={13} />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => void onRemove(n)}
                  className="text-danger hover:bg-danger-50 hover:text-danger dark:hover:bg-rose-950/40"
                  title={t('connect.action.remove')}
                >
                  <TrashIcon size={13} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PhoneNumberTable;
